/**
 * Item - Base class for all items
 */

import { ItemType, ResourceType } from '../types/enums';

export interface ItemConfig {
  id: string;
  name: string;
  description: string;
  type?: ItemType;
  stackable?: boolean;
  maxStack?: number;
  value: number;
  sprite?: string;
}

export class Item {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly type: ItemType;
  readonly stackable: boolean;
  readonly maxStack: number;
  readonly value: number;
  readonly sprite: string;

  constructor(config: ItemConfig) {
    this.id = config.id;
    this.name = config.name;
    this.description = config.description;
    this.type = config.type ?? ItemType.Resource;
    this.stackable = config.stackable ?? true;
    this.maxStack = config.maxStack ?? 99;
    this.value = config.value;
    this.sprite = config.sprite ?? config.id;
  }

  /** Use the item */
  use(): boolean {
    // Base implementation - override in subclasses
    return false;
  }

  /** Get item color for rendering */
  getColor(): string {
    switch (this.type) {
      case ItemType.Resource:
        return '#8b7355';
      case ItemType.Tool:
        return '#708090';
      case ItemType.Weapon:
        return '#cd5c5c';
      case ItemType.Armor:
        return '#4682b4';
      case ItemType.Consumable:
        return '#90ee90';
      case ItemType.Material:
        return '#daa520';
      default:
        return '#808080';
    }
  }
}

/**
 * ItemStack - Represents a stack of items
 */
export interface ItemStack {
  item: Item;
  quantity: number;
}

/**
 * Resource Item - Basic gatherable resources
 */
export class ResourceItem extends Item {
  readonly resourceType: ResourceType;

  constructor(config: ItemConfig & { resourceType: ResourceType }) {
    super({
      ...config,
      type: ItemType.Resource,
      stackable: true,
      maxStack: config.maxStack ?? 99
    });
    this.resourceType = config.resourceType;
  }
}

/**
 * Tool Item - Items used for gathering
 */
export class ToolItem extends Item {
  readonly toolPower: number;
  readonly toolType: 'axe' | 'pickaxe' | 'shovel' | 'hammer' | 'sickle' | 'drill';
  private durability: number;
  readonly maxDurability: number;

  constructor(config: ItemConfig & {
    toolPower: number;
    toolType: 'axe' | 'pickaxe' | 'shovel' | 'hammer' | 'sickle' | 'drill';
    durability: number;
  }) {
    super({
      ...config,
      type: ItemType.Tool,
      stackable: false,
      maxStack: 1
    });
    this.toolPower = config.toolPower;
    this.toolType = config.toolType;
    this.durability = config.durability;
    this.maxDurability = config.durability;
  }

  use(): boolean {
    if (this.durability > 0) {
      this.durability--;
      return true;
    }
    return false;
  }

  getDurability(): number {
    return this.durability;
  }

  getMaxDurability(): number {
    return this.maxDurability;
  }

  getDurabilityPercent(): number {
    return this.durability / this.maxDurability;
  }

  isBroken(): boolean {
    return this.durability <= 0;
  }

  repair(amount: number): void {
    this.durability = Math.min(this.maxDurability, this.durability + amount);
  }
}

/**
 * Weapon Type - Determines attack animation style
 */
export type WeaponType = 'sword' | 'spear' | 'dagger' | 'club' | 'axe' | 'scythe' | 'hammer';

/**
 * Ranged Weapon Type - Different gun categories
 */
export type RangedWeaponType = 'pistol' | 'rifle' | 'shotgun' | 'smg' | 'sniper' | 'laser' | 'plasma' | 'railgun';

/**
 * Weapon Item - Items used for combat
 */
export class WeaponItem extends Item {
  readonly damage: number;
  readonly attackSpeed: number;
  readonly range: number;
  readonly weaponType: WeaponType;
  private durability: number;
  readonly maxDurability: number;

  constructor(config: ItemConfig & {
    damage: number;
    attackSpeed: number;
    range: number;
    durability: number;
    weaponType?: WeaponType;
  }) {
    super({
      ...config,
      type: ItemType.Weapon,
      stackable: false,
      maxStack: 1
    });
    this.damage = config.damage;
    this.attackSpeed = config.attackSpeed;
    this.range = config.range;
    this.weaponType = config.weaponType ?? 'sword';
    this.durability = config.durability;
    this.maxDurability = config.durability;
  }

  use(): boolean {
    if (this.durability > 0) {
      this.durability--;
      return true;
    }
    return false;
  }

  getDurability(): number {
    return this.durability;
  }

  isBroken(): boolean {
    return this.durability <= 0;
  }
}

/**
 * Ranged Weapon Item - Guns and ranged weapons that fire projectiles
 */
export class RangedWeaponItem extends Item {
  readonly damage: number;
  readonly fireRate: number;       // Shots per second
  readonly range: number;
  readonly accuracy: number;       // 0-1, affects spread
  readonly projectileSpeed: number;
  readonly ammoType: string;       // ID of required ammo item
  readonly magazineSize: number;
  readonly reloadTime: number;     // Seconds
  readonly weaponType: RangedWeaponType;
  readonly projectileType: 'bullet' | 'shell' | 'laser' | 'plasma' | 'rail';
  readonly projectileColor: string;
  readonly tier: 'primitive' | 'modern' | 'futuristic';

  private durability: number;
  readonly maxDurability: number;
  private currentAmmo: number = 0;
  private isReloading: boolean = false;
  private reloadProgress: number = 0;

  constructor(config: ItemConfig & {
    damage: number;
    fireRate: number;
    range: number;
    accuracy: number;
    projectileSpeed: number;
    ammoType: string;
    magazineSize: number;
    reloadTime: number;
    durability: number;
    weaponType: RangedWeaponType;
    projectileType: 'bullet' | 'shell' | 'laser' | 'plasma' | 'rail';
    projectileColor?: string;
    tier: 'primitive' | 'modern' | 'futuristic';
  }) {
    super({
      ...config,
      type: ItemType.Weapon,
      stackable: false,
      maxStack: 1
    });
    this.damage = config.damage;
    this.fireRate = config.fireRate;
    this.range = config.range;
    this.accuracy = config.accuracy;
    this.projectileSpeed = config.projectileSpeed;
    this.ammoType = config.ammoType;
    this.magazineSize = config.magazineSize;
    this.reloadTime = config.reloadTime;
    this.weaponType = config.weaponType;
    this.projectileType = config.projectileType;
    this.projectileColor = config.projectileColor ?? '#ffff00';
    this.tier = config.tier;
    this.durability = config.durability;
    this.maxDurability = config.durability;
    this.currentAmmo = config.magazineSize; // Start with full magazine
  }

  /** Fire the weapon - returns true if shot was fired */
  fire(): boolean {
    if (this.isReloading || this.currentAmmo <= 0 || this.durability <= 0) {
      return false;
    }
    this.currentAmmo--;
    this.durability -= 0.5; // Small durability loss per shot
    return true;
  }

  /** Start reloading */
  startReload(): void {
    if (!this.isReloading && this.currentAmmo < this.magazineSize) {
      this.isReloading = true;
      this.reloadProgress = 0;
    }
  }

  /** Update reload progress */
  updateReload(deltaTime: number, availableAmmo: number): number {
    if (!this.isReloading) return 0;

    this.reloadProgress += deltaTime;
    if (this.reloadProgress >= this.reloadTime) {
      this.isReloading = false;
      this.reloadProgress = 0;

      // Calculate how much ammo to load
      const ammoNeeded = this.magazineSize - this.currentAmmo;
      const ammoToLoad = Math.min(ammoNeeded, availableAmmo);
      this.currentAmmo += ammoToLoad;
      return ammoToLoad; // Return amount consumed from inventory
    }
    return 0;
  }

  /** Cancel reload */
  cancelReload(): void {
    this.isReloading = false;
    this.reloadProgress = 0;
  }

  /** Set current ammo (for loading from save) */
  setCurrentAmmo(ammo: number): void {
    this.currentAmmo = Math.min(ammo, this.magazineSize);
  }

  getCurrentAmmo(): number {
    return this.currentAmmo;
  }

  getMagazineSize(): number {
    return this.magazineSize;
  }

  getIsReloading(): boolean {
    return this.isReloading;
  }

  getReloadProgress(): number {
    return this.reloadProgress / this.reloadTime;
  }

  getDurability(): number {
    return this.durability;
  }

  getMaxDurability(): number {
    return this.maxDurability;
  }

  isBroken(): boolean {
    return this.durability <= 0;
  }

  /** Get accuracy-adjusted direction spread */
  getSpreadAngle(): number {
    const maxSpread = (1 - this.accuracy) * 0.5; // Max spread in radians
    return (Math.random() - 0.5) * maxSpread;
  }
}

/**
 * Consumable Item - Items that can be consumed for effects
 */
export class ConsumableItem extends Item {
  readonly healthRestore: number;
  readonly staminaRestore: number;
  readonly effect?: string;

  constructor(config: ItemConfig & {
    healthRestore?: number;
    staminaRestore?: number;
    effect?: string;
  }) {
    super({
      ...config,
      type: ItemType.Consumable,
      stackable: true,
      maxStack: config.maxStack ?? 20
    });
    this.healthRestore = config.healthRestore ?? 0;
    this.staminaRestore = config.staminaRestore ?? 0;
    this.effect = config.effect;
  }

  use(): boolean {
    // Consumption handled elsewhere
    return true;
  }
}

/**
 * Equipment Slot - Where armor/equipment can be worn
 */
export type EquipmentSlot = 'head' | 'chest' | 'legs' | 'feet' | 'hands' | 'accessory';

/**
 * Equipment Bonuses - Stats provided by equipment
 */
export interface EquipmentBonuses {
  defense?: number;
  maxHealth?: number;
  maxStamina?: number;
  moveSpeed?: number;      // Percentage bonus (e.g., 0.1 = +10%)
  attackDamage?: number;
  attackSpeed?: number;    // Percentage bonus
  gatherSpeed?: number;    // Percentage bonus
}

/**
 * Armor Item - Wearable equipment that provides bonuses
 */
export class ArmorItem extends Item {
  readonly slot: EquipmentSlot;
  readonly defense: number;
  readonly bonuses: EquipmentBonuses;
  private durability: number;
  readonly maxDurability: number;

  constructor(config: ItemConfig & {
    slot: EquipmentSlot;
    defense: number;
    durability: number;
    bonuses?: EquipmentBonuses;
  }) {
    super({
      ...config,
      type: ItemType.Armor,
      stackable: false,
      maxStack: 1
    });
    this.slot = config.slot;
    this.defense = config.defense;
    this.durability = config.durability;
    this.maxDurability = config.durability;
    this.bonuses = config.bonuses ?? {};
  }

  getDurability(): number {
    return this.durability;
  }

  getMaxDurability(): number {
    return this.maxDurability;
  }

  getDurabilityPercent(): number {
    return this.durability / this.maxDurability;
  }

  isBroken(): boolean {
    return this.durability <= 0;
  }

  /** Take damage - reduces durability */
  takeDamage(amount: number): void {
    this.durability = Math.max(0, this.durability - amount * 0.1);
  }

  /** Get total defense including bonuses */
  getTotalDefense(): number {
    return this.defense + (this.bonuses.defense ?? 0);
  }
}

/**
 * Torch Item - A light source that can be toggled on/off
 */
export class TorchItem extends Item {
  private isLit: boolean = false;
  readonly lightRadius: number;
  readonly lightColor: string;
  private durability: number;
  readonly maxDurability: number;
  private burnRate: number; // durability loss per second when lit

  constructor(config: ItemConfig & {
    lightRadius?: number;
    lightColor?: string;
    durability: number;
    burnRate?: number;
  }) {
    super({
      ...config,
      type: ItemType.Tool,
      stackable: false,
      maxStack: 1
    });
    this.lightRadius = config.lightRadius ?? 4;
    this.lightColor = config.lightColor ?? 'rgba(255, 180, 80, 0.2)';
    this.durability = config.durability;
    this.maxDurability = config.durability;
    this.burnRate = config.burnRate ?? 0.5; // Lose 0.5 durability per second
  }

  /** Toggle the torch on/off */
  toggle(): boolean {
    if (this.durability <= 0) {
      this.isLit = false;
      return false;
    }
    this.isLit = !this.isLit;
    return true;
  }

  /** Update torch (burn durability when lit) */
  update(deltaTime: number): void {
    if (this.isLit && this.durability > 0) {
      this.durability -= this.burnRate * deltaTime;
      if (this.durability <= 0) {
        this.durability = 0;
        this.isLit = false;
      }
    }
  }

  use(): boolean {
    return this.toggle();
  }

  getIsLit(): boolean {
    return this.isLit;
  }

  setLit(lit: boolean): void {
    if (lit && this.durability <= 0) return;
    this.isLit = lit;
  }

  getDurability(): number {
    return this.durability;
  }

  getMaxDurability(): number {
    return this.maxDurability;
  }

  getDurabilityPercent(): number {
    return this.durability / this.maxDurability;
  }

  isBurntOut(): boolean {
    return this.durability <= 0;
  }

  getLightRadius(): number {
    return this.isLit ? this.lightRadius : 0;
  }

  getLightColor(): string {
    return this.lightColor;
  }
}
